import React from "react";
import { Box, CircularProgress, Switch } from "@mui/material";
import { COLORS } from "../../assets/theme";
import CustomLabel from "../CustomLabel";

export interface SettingsCardProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  sx?: any;
}

/**
 * Grouped settings card — a titled white panel whose rows are separated by a
 * hairline, the way a macOS System Settings group looks.
 */
export const SettingsCard: React.FC<SettingsCardProps> = ({
  title,
  description,
  children,
  sx,
}) => (
  <Box sx={{ mb: 3, ...sx }}>
    {title && (
      <CustomLabel
        text={title}
        fontWeight="bold"
        color={COLORS.typographyColor}
        sx={{ mb: description ? 0.25 : 1, ml: 0.5 }}
      />
    )}
    {description && (
      <CustomLabel
        text={description}
        fontSize={13}
        color={COLORS.placeHolderColor}
        sx={{ mb: 1, ml: 0.5 }}
      />
    )}
    <Box
      sx={{
        boxShadow: 2,
        borderRadius: 2,
        bgcolor: COLORS.WhiteBackground,
        overflow: "hidden",
        "& > *:not(:last-child)": {
          borderBottom: `1px solid ${COLORS.BorderColor}`,
        },
      }}>
      {children}
    </Box>
  </Box>
);

export interface SettingToggleRowProps {
  title: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Shows a spinner next to the switch while the change is being saved. */
  saving?: boolean;
  /** Extra line under the description, e.g. why the row is disabled. */
  hint?: string;
  /** Indents the row so it reads as belonging to the row above it. */
  nested?: boolean;
  /** Revealed under the row — usually the input the toggle controls. */
  children?: React.ReactNode;
}

/**
 * One switch row: label and description on the left, switch on the right.
 */
const SettingToggleRow: React.FC<SettingToggleRowProps> = ({
  title,
  description,
  checked,
  onChange,
  disabled = false,
  saving = false,
  hint,
  nested = false,
  children,
}) => (
  <Box
    sx={{
      px: { xs: 2, sm: 2.5 },
      py: 2,
      pl: nested ? { xs: 3.5, sm: 5 } : { xs: 2, sm: 2.5 },
      bgcolor: nested ? "rgba(0, 0, 0, 0.015)" : "transparent",
    }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
      }}>
      <Box sx={{ minWidth: 0, opacity: disabled ? 0.55 : 1 }}>
        <CustomLabel
          text={title}
          fontSize={15}
          fontWeight={600}
          color={COLORS.typographyColor}
        />
        {description && (
          <CustomLabel
            text={description}
            fontSize={13}
            color={COLORS.placeHolderColor}
            sx={{ mt: 0.25, lineHeight: 1.45, maxWidth: 620 }}
          />
        )}
        {hint && (
          <CustomLabel
            text={hint}
            fontSize={12}
            color={COLORS.ORANGE}
            sx={{ mt: 0.5, lineHeight: 1.45, maxWidth: 620 }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          mt: 0.25,
        }}>
        {saving && <CircularProgress size={14} thickness={5} />}
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
      </Box>
    </Box>

    {children && <Box sx={{ mt: 1.5 }}>{children}</Box>}
  </Box>
);

export default SettingToggleRow;
